import { jsx } from 'react/jsx-runtime';
import { noop } from '@fabric-app/utils/guards/noop';
import { useEffect, useContext, createContext, useState, useRef, useCallback } from 'react';

const SubDocumentSearchContext = /* @__PURE__ */ createContext({
  searchRegex: new RegExp(""),
  searchText: "",
  matchCase: false,
  wholeWords: false,
  currentMatch: 0,
  totalMatches: 0,
  isOpen: false,
  isAvailable: false,
  setSearchText: noop,
  setMatchCase: noop,
  setWholeWords: noop,
  goToMatch: noop,
  setOpen: noop,
  registerSearchHandlers: () => noop,
  onSearchStateChange: noop
});
function SubDocumentSearchProvider({ children }) {
  const [searchRegex, setSearchRegex] = useState(new RegExp(""));
  const [searchText, setSearchText] = useState("");
  const [matchCase, setMatchCase] = useState(false);
  const [wholeWords, setWholeWords] = useState(false);
  const [currentMatch, setCurrentMatch] = useState(0);
  const [totalMatches, setTotalMatches] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const handlers = useRef({
    onSearch: () => Promise.resolve({
      totalMatches: 0,
      currentIndex: null
    }),
    onGoToMatch: () => {
    }
  });
  const registerSearchHandlers = useCallback((newHandlers) => {
    handlers.current = {
      ...handlers.current,
      ...newHandlers
    };
    setIsAvailable(true);
    return () => {
      handlers.current = {
        onSearch: () => Promise.resolve({
          totalMatches: 0,
          currentIndex: null
        }),
        onGoToMatch: () => {
        }
      };
      setIsAvailable(false);
    };
  }, []);
  useEffect(() => {
    let aborted = false;
    if (searchText && handlers.current.onSearch) {
      const regexFlags = matchCase ? "g" : "gi";
      const escapedSearchText = searchText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(" ", "\\s");
      const regex = wholeWords ? new RegExp(`\\b${escapedSearchText}\\b`, regexFlags) : new RegExp(escapedSearchText, regexFlags);
      setSearchRegex(regex);
      handlers.current.onSearch(regex).then((result) => {
        if (!aborted && result) {
          setTotalMatches(result.totalMatches || 0);
          setCurrentMatch(result.currentIndex || 0);
        }
      }).catch((error) => {
        console.error(error);
      });
    } else if (!searchText) {
      setTotalMatches(0);
      setCurrentMatch(0);
      setSearchRegex(new RegExp(""));
      handlers.current.onSearch(null);
    }
    return () => {
      aborted = true;
    };
  }, [
    searchText,
    matchCase,
    wholeWords
  ]);
  const goToMatch = useCallback((index) => {
    if (index >= 0 && index < totalMatches && handlers.current.onGoToMatch) {
      setCurrentMatch(index);
      handlers.current.onGoToMatch(index);
    }
  }, [
    totalMatches
  ]);
  const setOpen = useCallback((open) => {
    setIsOpen(open);
  }, []);
  const onSearchStateChange = useCallback((state) => {
    setTotalMatches(state.totalMatches);
    setCurrentMatch(state.currentIndex || 0);
  }, []);
  const value = {
    searchRegex,
    searchText,
    matchCase,
    wholeWords,
    currentMatch,
    totalMatches,
    isOpen,
    isAvailable,
    setSearchText,
    setMatchCase,
    setWholeWords,
    goToMatch,
    setOpen,
    registerSearchHandlers,
    onSearchStateChange
  };
  return /* @__PURE__ */ jsx(SubDocumentSearchContext.Provider, {
    value,
    children
  });
}
function useSubDocumentSearch() {
  const context = useContext(SubDocumentSearchContext);
  return context;
}
function useRegisterSubDocumentSearch(searchHandlers) {
  const { registerSearchHandlers, onSearchStateChange } = useSubDocumentSearch();
  useEffect(() => {
    const unregister = registerSearchHandlers(searchHandlers);
    return unregister;
  }, [
    registerSearchHandlers,
    searchHandlers
  ]);
  return {
    onSearchStateChange
  };
}

export { SubDocumentSearchProvider, useRegisterSubDocumentSearch, useSubDocumentSearch };
//# sourceMappingURL=SubDocumentSearchContext.js.map
